import type { ShippedLocale } from './locale.ts';
import { addCatalog, hasCatalog } from './t.ts';

const warned = new Set<ShippedLocale>();

/**
 * Fetches a locale's catalog and puts it in front of English (spec §4.3).
 * English is bundled; every other catalog costs one request, made only by a
 * reader who needs it.
 *
 * `false` leaves that locale English: on a network error, an error status,
 * and a missing file, which looks like success on the wire because
 * `ServeDir` answers it with the app's HTML shell and a 200. Logged once per
 * locale, since a learner left in English is the thing this exists to fix.
 *
 * `main.tsx` holds the first paint on this, so a request that never settles
 * (a proxy holding the socket open) gives up after `timeoutMs` like any other
 * failure. The race is on a timer, not only the abort signal, so a fetcher
 * that ignores the signal still cannot keep the page blank.
 */
export async function loadCatalog(
  locale: ShippedLocale,
  fetcher: typeof fetch = fetch,
  timeoutMs = 8000,
): Promise<boolean> {
  if (locale === 'en' || hasCatalog(locale)) return true;
  const abort = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const expired = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      abort.abort();
      reject(new Error(`no answer in ${timeoutMs} ms`));
    }, timeoutMs);
  });
  try {
    const source = await Promise.race([
      (async () => {
        const res = await fetcher(`/locales/${locale}.ftl`, { signal: abort.signal });
        const text = await res.text();
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return text;
      })(),
      expired,
    ]);
    if (source.trimStart().startsWith('<')) throw new Error('answered with a page, not a catalog');
    const errors = addCatalog(locale, source);
    if (errors.length > 0) console.warn(`i18n: the ${locale} catalog has entries Fluent refused:`, ...errors);
    return true;
  } catch (error) {
    if (!warned.has(locale)) {
      warned.add(locale);
      console.warn(`i18n: the ${locale} catalog could not be loaded, so the interface stays English:`, error);
    }
    return false;
  } finally {
    clearTimeout(timer);
  }
}
